import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload } from '../../utils/security';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { UserPlus, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminUserAdd = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'partner'
    });
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            const sanitized = sanitizePayload(formData);
            const response = await fetchWithSecurity(`${API_URL}users.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'create_user', ...sanitized })
            });
            const result = await response.json();
            if (result.success) {
                toast.success(t('admin.userAdd.toastCreated') || 'მომხმარებელი წარმატებით შეიქმნა');
                setTimeout(() => navigate('/admin/users'), 1200);
            } else {
                toast.error(result.error);
            }
        } catch (error) {
            toast.error(t('admin.userAdd.toastError') || 'მომხმარებლის შექმნის შეცდომა');
        }
        setIsCreating(false);
    };

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={t('admin.userAdd.layoutTitle') || 'ახალი მომხმარებლის დამატება'}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <button className="shadcn-btn shadcn-btn-secondary" onClick={() => navigate('/admin/users')}>
                    <ArrowLeft size={16} /> {t('common.back') || 'მომხმარებლებში დაბრუნება'}
                </button>
            </div>

            <div className="shadcn-card max-w-lg mx-auto" style={{ maxWidth: '600px' }}>
                <div className="d-flex align-items-center gap-3 mb-4 pb-3 border-bottom border-secondary border-opacity-25">
                    <div className="p-2 rounded-3 bg-indigo bg-opacity-20 text-indigo" style={{ color: '#6366f1' }}>
                        <UserPlus size={24} />
                    </div>
                    <div>
                        <h2 className="text-dark fs-4 fw-bold mb-0">{t('admin.userAdd.title') || 'ანგარიშის შექმნა'}</h2>
                        <span className="text-muted small">{t('admin.userAdd.subtitle') || 'უსაფრთხო მონაცემები და წვდომის მართვა'}</span>
                    </div>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="form-group-label">{t('admin.userAdd.username') || 'მომხმარებლის სახელი'}</label>
                        <input 
                            type="text" 
                            name="username"
                            className="shadcn-input" 
                            value={formData.username}
                            onChange={handleInputChange}
                            required 
                            placeholder={t('admin.userAdd.usernamePlaceholder') || 'შეიყვანეთ მომხმარებლის სახელი...'}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="form-group-label">{t('admin.userAdd.password') || 'პაროლი'}</label>
                        <div className="position-relative">
                            <input 
                                type={showPassword ? 'text' : 'password'} 
                                name="password"
                                className="shadcn-input pe-5" 
                                value={formData.password}
                                onChange={handleInputChange}
                                required 
                                placeholder={t('admin.userAdd.passwordPlaceholder') || 'შეიყვანეთ უსაფრთხო პაროლი...'}
                            />
                            <button 
                                type="button" 
                                className="btn position-absolute right-0 top-50 translate-middle-y text-muted me-2 border-0 bg-transparent" 
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    <div className="mb-5">
                        <label className="form-group-label">{t('admin.userAdd.role') || 'როლი და წვდომები (RBAC)'}</label>
                        <select 
                            name="role"
                            className="shadcn-select" 
                            value={formData.role}
                            onChange={handleInputChange}
                        >
                            <option value="partner">{t('admin.userAdd.rolePartner') || 'პარტნიორი (კლიენტის დეშბორდი)'}</option>
                            <option value="blog_manager">{t('admin.userAdd.roleBlogManager') || 'ბლოგის მენეჯერი (კონტენტის რედაქტორი)'}</option>
                            <option value="super_admin">{t('admin.userAdd.roleSuperAdmin') || 'სუპერ ადმინი (სრული წვდომა)'}</option>
                        </select>
                        <p className="text-muted smallest mt-2">
                            {t('admin.userAdd.roleHelper') || 'მიანიჭეთ შესაბამისი როლი მომხმარებლის ფუნქციებიდან გამომდინარე.'}
                        </p>
                    </div>
                    
                    <div className="d-flex gap-3">
                        <button type="button" className="shadcn-btn shadcn-btn-secondary w-50" onClick={() => navigate('/admin/users')}>
                            {t('common.cancel') || 'გაუქმება'}
                        </button>
                        <button type="submit" className="shadcn-btn shadcn-btn-primary w-50" disabled={isCreating}>
                            <UserPlus size={16} />
                            <span>{isCreating ? (t('admin.userAdd.creating') || 'იქმნება...') : (t('admin.userAdd.create') || 'ანგარიშის შექმნა')}</span>
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default AdminUserAdd;
